import { CircularProgress } from '@material-ui/core'
import { ZIO } from '@mxt/zio'
import { useLoading } from '@mxt/zio-react'
import {
  AppContext,
  assets,
  AuthService,
  Divider,
  ErrorHandling,
  Form,
  GeneralService,
  getLanguage,
  Permission,
  RadioButton,
  RBAC,
  RoundedButton,
  SelectOption,
  SelectSearch,
  SubmissionService,
  useMobile,
  OfficeCode,
  isCasualLabor,
  UserRoles,
  AgentInfo,
  sharedStyle
} from '@pulseops/common'
import { useIsFocused } from '@react-navigation/native'
import { StackScreenProps } from '@react-navigation/stack'
import { pipe } from 'fp-ts/function'
import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { StyleSheet, Text, TouchableOpacity, useWindowDimensions, View } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { Input, SectionCol, SectionRow } from '../common/src/component'
import { StaffSubmissionStackParamList } from './StaffSubmissionStackParamList'
import { ClaimService } from './claim'
import { isEmpty } from 'lodash'
import { BehaviorSubject } from 'rxjs'

type StaffSubmissionForm = {
  officeCode: SelectOption | null
  policy: string
  primaryId: string
  idNumber: string
  POName: string
}

const generateUUID = () => {
  let uuidValue = '',
    k,
    randomValue
  for (k = 0; k < 32; k++) {
    randomValue = (Math.random() * 16) | 0

    if (k === 8 || k === 12 || k === 16 || k === 20) {
      uuidValue += '-'
    }
    uuidValue += (k === 12 ? 4 : k === 16 ? (randomValue & 3) | 8 : randomValue).toString(16)
  }
  return uuidValue
}

const PONameErrorMessageSubject = new BehaviorSubject('')


type Props = StackScreenProps<StaffSubmissionStackParamList, 'StaffSubmissionScreen'>

export const StaffSubmissionScreen = (props: Props) => {
  const { width } = useWindowDimensions()
  const { t, i18n } = useTranslation('common')
  const at = useTranslation().t
  const [selectedPolicy, setSelectedPolicy] = React.useState<string>('')
  const [clientEmail, setClientEmail] = React.useState<string>('')
  const [cells, setCells] = React.useState<TableData[][]>([])
  const [loading, bindLoader] = useLoading(false)
  const [loadingClaim, bindLoaderClaim] = useLoading(false)
  const [officeCodeSelected, setOfficeCodeSelected] = React.useState<OfficeCode>()
  const [isGA, setIsGA] = React.useState(false)
  const { isMobile } = useMobile()
  const [isEnabledPOFullName, setIsEnabledPOFullName] = React.useState(false)

  const isFocused = useIsFocused()
  const { changeBreadcrumb, showToast } = React.useContext(AppContext.AppContextInstance)

  const form = useForm<StaffSubmissionForm>()

  const { watch, trigger, formState, control, setValue } = form

  const getError = Form.getError(formState)
  const { policy, officeCode, idNumber, POName } = watch()

  const register = Form.register(form)

  pipe(
    AuthService.userInfo,
    ZIO.flatMap((userData) => {
      const casualLaborInfo =
        userData.isGaLogin && !isCasualLabor()
          ? pipe(
            RBAC.getPermission(userData.email),
            ZIO.map((emailList) => {
              let isCasualLabor: boolean = false
              if (emailList && emailList.groups) {
                isCasualLabor = emailList.groups.includes(UserRoles.CasualLabor)
              }
              return {
                ...userData,
                isCasualLabor: isCasualLabor
              }
            })
          )
          : ZIO.effect(() => ({
            ...userData,
            isCasualLabor: isCasualLabor()
          }))
      return casualLaborInfo
    }),
    ZIO.tap((x) => {
      setIsGA(x.isGaLogin && !x.isCasualLabor)
      return ZIO.unit
    }),
    ZIO.flatMap((x) =>
      x.officeCode !== null
        ? pipe(
          GeneralService.getOfficeCode(x.officeCode),
          ZIO.tap((y) => {
            const officeCode = y.body[0]
            if (officeCode) {
              setOfficeCodeSelected(officeCode)
              const option = {
                label: getLanguage() === 'en' ? officeCode.nameEn : officeCode.nameVi,
                value: officeCode.code
              }
              form.setValue('officeCode', option)
            }
            return ZIO.unit
          })
        )
        : ZIO.succeed(null)
    ),
    ErrorHandling.runDidMount()
  )
  // const officeCodes: OfficeCode[] = pipe(GeneralService.getOfficeCodes('CSC'), ErrorHandling.runDidMount([]))
  const officeCodes: OfficeCode[] = pipe(
    GeneralService.getALLOfficeList(),
    ZIO.map((officeList) => {
      return officeList && officeList.length > 0 ? officeList.filter((x) => ['CSC', 'CCE', 'PCM', 'CCR'].includes(x.type)) : []
    }),
    ErrorHandling.runDidMount([])
  )

  const permissions: string[] | null = pipe(RBAC.permissions, ErrorHandling.runDidMount())

  React.useEffect(() => {
    trigger().then()
    if (!isEmpty(idNumber)) {
      setValue('idNumber', '')
    }
    if (isGA) {
      policy.length > 9 && setIsEnabledPOFullName(true)
      form.setValue('POName', '')
    }
  }, [policy])

  const reset = () => {
    setSelectedPolicy('')
    setClientEmail('')
    setCells([])
  }

  React.useEffect(() => {
    if (isFocused) {
      changeBreadcrumb([
        {
          title: '',
          navigate: () => {
            props.navigation.replace('HomeScreen')
          }
        },
        { title: at('Submission:CustomerServicing'), navigate: null }

      ])
    }
  }, [isFocused])

  useEffect(() => {
    if (!isEmpty(idNumber)) {
      setValue('idNumber', '')
    }
  }, [policy])

  React.useEffect(() => {
    const customedEmail = clientEmail.replace(/\s+/g, '')
    if (isFocused && selectedPolicy && selectedPolicy.length !== 9 && !customedEmail) {
      showToast(t('message:MS020003', { policyNumber: selectedPolicy }), 'error', undefined, 3000)
    }
  }, [selectedPolicy])

  const getPolicy = () =>
    pipe(
      policy && policy.length === 8
        ? SubmissionService.searchPolicy(policy)
        : SubmissionService.searchPolicyInquiry(policy),
      ZIO.tap((p) => {
        if (p.length > 0) {
          const { policyNum, clientName, idNum, laName, primaryId, clientEmail } = p[0]
          const customedIDNumber = idNum && idNum.length > 3
            ? `${idNum.slice(0, idNum.length - 3).replace(/./g, '*')}${idNum.slice(idNum.length - 3)}`
            : `****${idNum}`
          setClientEmail(clientEmail || '')
          setSelectedPolicy(policyNum)
          setValue('primaryId', primaryId)
          setCells([
            [
              {
                inputType: 'radio',
                value: true
              },
              {
                value: policyNum
              },
              {
                value: customedIDNumber
              },
              {
                value: clientName
              },
              {
                value: laName
              }
            ]
          ])
        } else {
          //NOTE: try case CMND has length 8 or 9
          onSearchIDNumber()
          return ZIO.unit
        }
        return ZIO.unit
      }),
      ZIO.tapError((_) => {
        //NOTE: try case CMND has length 8 or 9
        onSearchIDNumber()
        return ZIO.unit
      }),
      bindLoader,
      ZIO.unsafeRun({})
    )

  const onSearchIDNumber = () => {
    if (isGA && !POName) {
      setIsEnabledPOFullName(true)
      reset()
      setValue('primaryId', '')
      PONameErrorMessageSubject.next(t('message:MS020126'))
    } else {
      pipe(
        SubmissionService.searchIDNumber(policy),
        ZIO.tap((p) => {
          if (p.length > 0) {
            //NOTE: choose default value is the first row
            const { policyNum, primaryId, clientEmail : POEmail } = p[0]
            const customedPOName = isGA ? POName.trimLeft().trimRight() : ''
            const isInvalidPOName = isGA ? p.some((item) => item.clientName !== customedPOName) : false
            if (isGA && isInvalidPOName) {
              setIsEnabledPOFullName(true)
              reset()
              setValue('primaryId', '')
              PONameErrorMessageSubject.next(t('message:MS020125'))
            } else {
              setIsEnabledPOFullName(false)
              setClientEmail(POEmail || '')
              setSelectedPolicy(policyNum)
              setValue('primaryId', primaryId) 
              PONameErrorMessageSubject.next('')

              const cells: TableData[][] = p.map((item, index) => {
                const customedIDNumber = item.idNum && item.idNum.length > 3
                  ? `${item.idNum.slice(0, item.idNum.length - 3).replace(/./g, '*')}${item.idNum.slice(item.idNum.length - 3)}`
                  : `****${item.idNum}`
                return [
                  {
                    inputType: 'radio',
                    value: item.policyNum === policyNum && index === 0
                  },
                  {
                    value: item.policyNum
                  },
                  {
                    value: customedIDNumber
                  },
                  {
                    value: item.clientName
                  },
                  {
                    value: item.laName
                  },
                  {
                    value: item.clientEmail
                  }
                ]
              })
              setCells(cells)
            }

          } else {
            showToast(t('message:MS030029'), 'info')
            reset()
          }
          return ZIO.unit
        }),
        ZIO.tapError((_) => {
          showToast(t('message:MS030029'), 'info')
          reset()
          return ZIO.unit
        }),
        bindLoader,
        ZIO.unsafeRun({})
      )
    }

  }

  const headers = [`${t('Select')}`, `${t('Policy')}`, `${t('IDNumber')}`, `${t('PolicyOwner')}`, `${t('LifeAssured')}`]

  const checkRoles = (role: Permission): boolean => {
    if (isGA && !officeCodeSelected) {
      return true
    }

    const claimAction = [
      Permission.ViewPolicyDetailSubmission,
      Permission.ViewFunctionListClaimsSubmission
    ]
    const checker =
      !selectedPolicy ||
      (role === Permission.ViewFunctionListClaimsSubmission && selectedPolicy.length === 8) ||
      (!claimAction.includes(role) && selectedPolicy.length === 9)
    return checker || !permissions?.includes(role)
  }

  const isInValidPermissions = (role: Permission[]) => {
    if (isGA && !officeCodeSelected) {
      return true
    }
    const claimAction = [Permission.ViewPolicyDetailSubmission, Permission.ViewFunctionListClaimsSubmission]
    const checker =
      !selectedPolicy ||
      (role.some((p) => p === Permission.ViewFunctionListClaimsSubmission) && selectedPolicy.length === 8) ||
      (!claimAction.some((p) => role.includes(p as Permission)) && selectedPolicy.length === 9)
    return checker || !permissions?.some((p) => role.includes(p as Permission))
  }

  const onPolicySearch = () => {
    PONameErrorMessageSubject.next('')
    if (!!policy && !loading && !getError('policy')) {
      setIsEnabledPOFullName(false)
      reset()
      if (isGA && policy.length === 8) {
        getAgentInfo(policy)
      } else {
        if (policy.length === 8 || policy.length === 9) {
          getPolicy()
        }
        if (policy.length > 9) {
          onSearchIDNumber()
        }
        form.clearErrors('officeCode')
      }
    }
  }

  const checkOfficeCode = () => {
    if (!officeCode) {
      form.setError('officeCode', { message: t('message:MS020001', { field: t('common:Office').toLowerCase() }) })
      return false
    } else {
      return true
    }
  }

  const onSelectedChanged = (data: TableData, index: number) => {
    if (!data.value) {
      //NOTE: Clone and clean the selected radio button
      const newCells: TableData[][] = [...cells].map((item) => [
        {
          inputType: 'radio',
          value: false
        },
        {
          value: item[1].value
        },
        {
          value: item[2].value
        },
        {
          value: item[3].value
        },
        {
          value: item[4].value
        },
        {
          value: item[5].value
        }
      ])
      const newSelectedPolicy = newCells[index][1].value as string
      newCells[index][0] = {
        inputType: 'radio',
        value: true
      }
      if (newSelectedPolicy) {
        setSelectedPolicy(newSelectedPolicy)
      }
      if (newCells[index].length >= 6) {
        setClientEmail(newCells[index][5].value as string)
      }
      setCells(newCells)
    }
  }

  i18n.on('languageChanged', (lang) => {
    if (officeCodeSelected) {
      form.setValue('officeCode.label', lang === 'en' ? officeCodeSelected.nameEn : officeCodeSelected.nameVi)
    }
  })

  const renderIDNumber = () => {
    return isGA && (
      <SectionCol sectionStyles={sharedStyle.col_4}>
        <Controller
          control={control}
          name={'idNumber'}
          // rules={{
          //   validate: {
          //     minlength24: (idNumber) => (isGA && idNumber?.length > 24) || `${t('common:MS090001')}`
          //   },
          //   pattern: {
          //     value: /^[^*&$#\\<>|~`!@%^()/';.,:/[/\]\"+=_-]*$/g,
          //     message: t('message:MS050223')
          //   }
          // }}
          render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
            return (
              <Input
                // containerStyle={{ flex: 2 }}
                placeholder={t('IDNumberSearch')}
                errorMessage={getError('idNumber')}
                maxLength={24}
                onChange={(val) => {
                  const pattern = /^[^*&$#\\<>|~`!@%^()';.,:[\]\"+=_]*$/g
                  if (pattern.test(val)) {
                    onChange(val)
                  }
                }}
                value={value}
              />
            )
          }}
        />
      </SectionCol>
    )
  }

  const getAgentInfo = (policyNum: string) =>
    pipe(
      SubmissionService.getAgentInfo(policyNum),
      ZIO.flatMap((res) => {
        if (res.office !== officeCodeSelected?.code && isEmpty(idNumber)) {
          showToast(
            t('message:MS990133'),
            'error',
            {
              horizontal: 'right',
              vertical: 'top'
            },
            10000
          )
          // setCells([])
          return ZIO.unit
        } else if (res.office === officeCodeSelected?.code) {
          if (policyNum.length === 8 || policyNum.length === 9) {
            getPolicy()
          }
          if (policyNum.length > 9) {
            onSearchIDNumber()
          }
          return ZIO.unit
        } else {
          const prms = {
            policyNo: policyNum,
            idNumber: idNumber,
            officeCode: officeCode?.value
          }
          const data = Buffer.from(JSON.stringify(prms)).toString('base64')
          getPolicyGA(data, policyNum)
          return ZIO.unit
        }
      }),
      ZIO.tapError((_) => {
        return ZIO.unit
      }),
      ZIO.catchAll((error) => {
        showToast(t('message:MS030029'), 'info')
        reset()
        return ZIO.unit
      }),
      bindLoader,
      ZIO.unsafeRun({})
    )

  const getPolicyGA = (data: string, policyNum: string) =>
    pipe(
      SubmissionService.getPolicyByGA(data, policyNum),
      ZIO.tap((p) => {
        if (p.length > 0) {
          const { policyNum, clientName, idNum, laName, primaryId, clientEmail } = p[0]
          const customedIDNumber = idNum && idNum.length > 3
            ? `${idNum.slice(0, idNum.length - 3).replace(/./g, '*')}${idNum.slice(idNum.length - 3)}`
            : `****${idNum}`
          setClientEmail(clientEmail || '')
          setSelectedPolicy(policyNum)
          setValue('primaryId', primaryId)
          setCells([
            [
              {
                inputType: 'radio',
                value: true
              },
              {
                value: policyNum
              },
              {
                value: customedIDNumber
              },
              {
                value: clientName
              },
              {
                value: laName
              }
            ]
          ])
        } else {
          //NOTE: try case CMND has length 8 or 9
          // onSearchIDNumber()
          showToast(t('message:MS030029'), 'info')
          reset()
          return ZIO.unit
        }
        return ZIO.unit
      }),
      ZIO.tapError((_) => {
        //NOTE: try case CMND has length 8 or 9
        // onSearchIDNumber()
        reset()
        showToast(t('message:MS030029'), 'info')
        return ZIO.unit
      }),
      bindLoader,
      ZIO.unsafeRun({})
    )
  const onChangePOName = (val: string) => {
    !!val && PONameErrorMessageSubject.next('')
  }

  return (
    <View style={{ backgroundColor: '#fff', height: '100%' }}>
      <ScrollView
        style={{
          display: 'flex',
          // width: width <= 1440 ? width : 1440,
          padding: 10
        }}
      >
        <SectionRow>
          <SectionCol sectionStyles={isGA ? sharedStyle.col_4 : sharedStyle.col_6}>
            <Input
              {...register('policy', {
                required: `${t('message:MS020001', { field: t('Policy/Proposal/IDNumber') })}`,
                validate: {
                  minlength8: (policy) => policy?.length > 7 || `${t('common:MS090001')}`
                }
              })}
              suffixIcon={() => (
                <TouchableOpacity
                  style={{ marginHorizontal: 15 }}
                  onPress={onPolicySearch}
                  disabled={!policy || policy.length <= 7}
                >
                  {loading ? (
                    <CircularProgress color={'secondary'} size={25} />
                  ) : policy && policy.length > 7 ? (
                    <assets.Search20Icon />
                  ) : (
                    <assets.SearchDisableIcon />
                  )}
                </TouchableOpacity>
              )}
              numberOnly={true}
              placeholder={t('Policy/Proposal/IDNumber')}
              errorMessage={getError('policy')}
              maxLength={24}
              disabledSearch={!permissions?.includes(Permission.SearchSubmission)}
              onIconPressed={() => {
                policy && policy.length > 7 && onPolicySearch()
              }}
            />
          </SectionCol>
          {renderIDNumber()}
          <SectionCol sectionStyles={isGA ? sharedStyle.col_4 : sharedStyle.col_6}>
            <Controller
              key={`${officeCode?.label}-${officeCode?.value}`}
              control={control}
              name={'officeCode'}
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => {
                return (
                  <SelectSearch
                    value={value}
                    onChange={(value) => {
                      form.clearErrors('officeCode')
                      setOfficeCodeSelected(officeCodes.find((item) => item.code === value?.value))
                      onChange(value)
                    }}
                    onBlur={onBlur}
                    label={''}
                    options={officeCodes.map((item) => ({
                      label: getLanguage() === 'en' ? item.nameEn : item.nameVi,
                      value: item.code
                    }))}
                    disableUnderline={true}
                    placeholder={t('ChooseOffice')}
                    disabled={isGA}
                    prefixIcon={<assets.LocationPinIcon />}
                    errorMessage={error?.message}
                    inputStyle={[
                      styles.selectSearchStyle,
                      officeCode === null ? { borderWidth: 1, borderColor: 'red' } : null
                    ]}
                    buildLabel={(select) => `${select.value} - ${select.label}`}
                    popupIcon={<assets.ArrowDownDropdownIcon />}
                    inputWhenFocus={true}
                  />
                )
              }}
            />
          </SectionCol>
        </SectionRow>
        {isGA && (
          <SectionRow sectionStyles={styles.secondLine}>
            <SectionCol>
              <Controller
                control={form.control}
                name={"POName"}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    placeholder={t('PolicyOwnerFullName')}
                    disabled={!isEnabledPOFullName}
                    onChange={(val) => {
                      onChange(val)
                      onChangePOName(val)
                    }}
                    errorMessage={error?.message}
                    value={value}
                  />
                )}
              />
              <Text style={styles.errorText}>{PONameErrorMessageSubject.value}</Text>
            </SectionCol>
          </SectionRow>
        )}
        {cells.length === 0 && (
          <Text style={{ display: 'flex', justifyContent: 'center', color: 'gray', marginTop: 51, marginBottom: 55 }}>
            {t('SearchToSeePolicy')}
          </Text>
        )}

        {cells.length !== 0 && (
          <View style={{ marginLeft: 15, marginRight: 15, marginTop: 40, marginBottom: 88 }}>
            <Table headers={headers} cells={cells} onSelectedChanged={onSelectedChanged} />
          </View>
        )}

        <View style={[styles.btnContainer]}>
          <RoundedButtonCustom
            text={t('PolicyDetail')}
            onPress={() => {
              if (checkOfficeCode()) {
                props.navigation.navigate('PolicyDetail', {
                  policyNum: selectedPolicy,
                  policyOwner: cells[0][3].value?.toString() || '-',
                  officeCode: watch('officeCode')?.value || '-',
                  officeName: watch('officeCode')?.label || '-',
                  primaryId: watch('primaryId')
                })
              }
            }}
            disable={checkRoles(Permission.ViewPolicyDetailSubmission)}
          />
          <RoundedButtonCustom text={t('PayPremium')} onPress={() => null} disable={true} />
          <RoundedButtonCustom
            text={t('ClaimServices')}
            onPress={() => {
              if (checkOfficeCode()) {
                pipe(
                  ClaimService.getDataAccess(selectedPolicy, generateUUID()),
                  ZIO.map((data) => {
                    if (data !== null) {
                      props.navigation.navigate('ClaimStack', {
                        screen: 'ClaimListScreen',
                        params: {
                          policyNum: selectedPolicy,
                          policyOwner: cells[0][3].value?.toString() || '-',
                          officeCode: watch('officeCode')?.value || '-',
                          officeName: watch('officeCode')?.label || '-',
                          clientNumber: cells[0][2].value?.toString() || ''
                        }
                      })
                    }
                  }),
                  bindLoaderClaim,
                  ZIO.unsafeRun({})
                )
              }
            }}
            disable={checkRoles(Permission.ViewFunctionListClaimsSubmission)}
            loading={loadingClaim}
          />
        </View>
        <View style={styles.btnContainer}>
          <RoundedButtonCustom
            text={t('PolicyServices')}
            onPress={() => {
              if (checkOfficeCode()) {
                props.navigation.navigate('PSSubmissionStack', {
                  screen: 'PSSubmissionListScreen',
                  params: {
                    policyNum: selectedPolicy,
                    policyOwner: cells[0][3].value?.toString() || '-',
                    officeCode: watch('officeCode')?.value || '-',
                    officeName: watch('officeCode')?.label || '-',
                    primaryId: watch('primaryId')
                  }
                })
              }
            }}
            disable={
              isInValidPermissions([
                Permission.ViewFunctionListPSSubmission,
                Permission.ViewFuncListAlterJournalFunctionListPS,
                Permission.ViewFuncListPayoutJournalFunctionListPS,
                Permission.ViewFunctionListPSSubmissionCCE
              ])
            }
          />
          <RoundedButtonCustom
            text={t('InquiryComplaint')}
            onPress={() => {
              if (checkOfficeCode()) {
                props.navigation.navigate('ComplainInquiryScreen', {
                  policyNum: selectedPolicy,
                  policyOwner: cells[0][3].value?.toString() || '-',
                  officeCode: watch('officeCode')?.value || null,
                  officeName: watch('officeCode')?.label || null,
                  primaryId: watch('primaryId')
                })
              }
            }}
            disable={checkRoles(Permission.ViewFunctionListCHCSSubmission)}
          />
          <RoundedButtonCustom text={t('PruRewards')} disable={true} />
        </View>
        {/* 360 Inquiry */}
        <View style={styles.btnContainer}>
          <RoundedButtonCustom
            text={t('menu:360Inquiry')}
            onPress={() => {
              if (checkOfficeCode()) {
                props.navigation.navigate('InquiryStack', {
                  screen: 'CEPPolicyDetailScreen',
                  params: {
                    policyNum: selectedPolicy,
                    policyOwner: cells[0][3].value?.toString() || '-',
                    officeCode: watch('officeCode')?.value || '-',
                    officeName: watch('officeCode')?.label || '-',
                    primaryId: watch('primaryId'),
                    clientNumber: cells[0][2].value?.toString() || '-',
                    isGAstaff: isGA ? '1' : '0',
                    isSubmission: 'true',
                    proposalNo: selectedPolicy
                  }
                })
              }
            }}
            disable={checkRoles(Permission.View360InquirySubmission)}
          />
          {/* AML Request */}
          <RoundedButtonCustom
            text={t('menu:AmlRequest')}
            onPress={() => {
              if (checkOfficeCode()) {
                props.navigation.navigate('AmlRequestStack', {
                  screen: 'AmlRequestListScreen',
                  params: {
                    policyNum: watch('policy'),
                    policyOwner: cells[0][3].value?.toString() || '-',
                    officeCode: watch('officeCode')?.value || '-',
                    officeName: watch('officeCode')?.label || '-'
                  }
                })
              }
            }}
            disable={checkRoles(Permission.ViewAmlRequestCustomerServicing)}
          />
          <View style={[styles.btnRounded, { width: isMobile ? '100%' : '25%', marginBottom: isMobile ? 10 : 0 }]} />
        </View>
      </ScrollView>
    </View>
  )
}

const RoundedButtonCustom = ({
  text,
  disable,
  onPress,
  loading
}: {
  text: string
  disable?: boolean
  onPress?: () => void
  loading?: boolean
}) => {
  const { isMobile } = useMobile()
  return loading === true ? (
    <View
      style={[
        styles.btnRounded,
        { width: isMobile ? '100%' : '25%', marginBottom: isMobile ? 10 : 0, alignItems: 'center' }
      ]}
    >
      <CircularProgress color="secondary"></CircularProgress>
    </View>
  ) : (
    <RoundedButton
      disabled={disable}
      text={text}
      activeOpacity={0.7}
      style={[styles.btnRounded, { width: isMobile ? '100%' : '25%', marginBottom: isMobile ? 10 : 0 }]}
      textStyle={styles.textStyle}
      showBorder={true}
      textColorEnable="#ED1B2E"
      textColorDisable="gray"
      borderColorEnable="#ED1B2E"
      borderColorDisable="gray"
      onPress={onPress}
    />
  )
}

interface TableData {
  inputType?: 'text' | 'radio' // text by default
  value?: number | string | boolean
}

interface CellProps {
  data: TableData[]
  rowIndex: number
}

interface TableProps {
  headers: string[]
  cells: TableData[][]
  onSelectedChanged?: (data: TableData, index: number) => void
}

const Table: React.FC<TableProps> = ({ headers, cells, onSelectedChanged }) => {
  const Cell: React.FC<CellProps> = ({ data, rowIndex }) => {
    return (
      <View
        style={[
          { borderBottomWidth: 2, borderBottomColor: '#EEEEEE' },
          { flex: 1, alignSelf: 'stretch', flexDirection: 'row', minHeight: 50, paddingLeft: 15, paddingVertical: 15 }
        ]}
      >
        {data.map((d, i) => (
          <View
            key={i}
            style={{
              flex: 1,
              alignSelf: 'stretch'
            }}
          >
            {(!d.inputType || d.inputType === 'text') && <Text style={styles.tableTextStyle}>{d.value}</Text>}
            {d.inputType === 'radio' && (
              <RadioButton
                onChange={() => {
                  onSelectedChanged && onSelectedChanged(d, rowIndex)
                }}
                selected={d.value === true}
              />
            )}
          </View>
        ))}
      </View>
    )
  }

  const renderHeader = (data: (string | number)[]) => (
    <View
      style={{
        flex: 1,
        alignSelf: 'stretch',
        flexDirection: 'row',
        minHeight: 74,
        backgroundColor: '#E5EAEF',
        paddingLeft: 15
      }}
    >
      {data.map((d, i) => (
        <View
          key={i}
          style={{
            flex: 1,
            alignSelf: 'stretch',
            justifyContent: 'center'
          }}
        >
          <Text style={{ fontWeight: 'bold', fontSize: 15, textTransform: 'capitalize' }}>{d}</Text>
        </View>
      ))}
    </View>
  )

  return (
    <View style={{ borderRadius: 15, borderWidth: 1, borderColor: '#D3DCE6', overflow: 'hidden' }}>
      {renderHeader(headers)}
      {cells.map((r, i) => (
        <React.Fragment key={i}>
          <Cell data={r} rowIndex={i} />
        </React.Fragment>
      ))}
    </View>
  )
}

const styles = StyleSheet.create({
  searchSection: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff'
  },
  inputContainer: {
    marginVertical: 20,
    marginLeft: 30,
    marginRight: 30
  },
  textStyle: {
    fontSize: 15,
    fontWeight: 'bold'
  },
  tableTextStyle: {
    fontSize: 16
  },
  btnContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    paddingLeft: 30,
    paddingRight: 30,
    marginBottom: 30
  },
  btnRounded: {
    height: 46,
    borderRadius: 20
  },
  selectSearch: {
    flex: 3
  },
  selectSearchStyle: {
    shadowColor: 'grey',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 6,
    borderRadius: 8,
    paddingVertical: 10,
    height: 46,
    paddingRight: 10
  },
  secondLine: {
    marginTop: 20
  },
  errorText: {
    color: 'red'
  }
})
